import { articles } from '@/mocks/articles';
import { SITE_URL, SITE_NAME, FAVICON_URL, HERO_IMAGE } from './meta';
import type { SeoData } from './meta';

const SITE_DESCRIPTION =
  'Explore the AllChinaBuy Spreadsheet with organized product finds, QC guides, shipping information, reviews, comparisons and practical buying tips.';

const SITE_KEYWORDS =
  'AllChinaBuy Spreadsheet, AllChinaBuy spreadsheet 2026, AllChinaBuy spreadsheet link, AllChinaBuy spreadsheet list, AllChinaBuy finds, AllChinaBuy QC, AllChinaBuy shipping';

const CONTENT_DATE = '2026-08-25';

export interface PageSeo {
  seo: SeoData;
  jsonLd: object | null;
}

function orgReference() {
  return {
    '@type': 'Organization',
    '@id': SITE_URL + '/#organization',
    name: SITE_NAME,
    url: SITE_URL + '/',
    logo: { '@type': 'ImageObject', url: FAVICON_URL },
  };
}

function websiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebSite',
        '@id': SITE_URL + '/#website',
        url: SITE_URL + '/',
        name: SITE_NAME,
        description: SITE_DESCRIPTION,
        inLanguage: 'en',
        publisher: { '@id': SITE_URL + '/#organization' },
      },
      orgReference(),
    ],
  };
}

function articleSchema(slug: string, title: string, meta: string, image: string) {
  const url = SITE_URL + '/article/' + slug;
  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: title,
    description: meta,
    image,
    url,
    datePublished: CONTENT_DATE,
    dateModified: CONTENT_DATE,
    inLanguage: 'en',
    author: { '@id': SITE_URL + '/#organization' },
    publisher: { '@id': SITE_URL + '/#organization' },
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
  };
}

function webPageSchema(name: string, description: string, path: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name,
    description,
    url: SITE_URL + path,
    inLanguage: 'en',
  };
}

const staticPages: Record<string, PageSeo> = {
  '/': {
    seo: {
      title: 'AllChinaBuy Spreadsheet 2026 — Finds, Guides, QC & Shipping',
      description: SITE_DESCRIPTION,
      keywords: SITE_KEYWORDS,
      canonical: SITE_URL + '/',
      ogType: 'website',
      ogImage: HERO_IMAGE,
      twitterCard: 'summary_large_image',
      twitterImage: HERO_IMAGE,
    },
    jsonLd: websiteSchema(),
  },
  '/spreadsheet': {
    seo: {
      title: 'AllChinaBuy Spreadsheet Categories — Shoes, Hoodies, Bags & More',
      description:
        'Browse every AllChinaBuy Spreadsheet category, including shoes, hoodies, t-shirts, jackets, bags, accessories and more, with direct links to each product category.',
      keywords:
        'AllChinaBuy Spreadsheet categories, AllChinaBuy spreadsheet shoes, AllChinaBuy spreadsheet hoodies, AllChinaBuy spreadsheet bags, AllChinaBuy spreadsheet clothing',
      canonical: SITE_URL + '/spreadsheet',
      ogType: 'website',
      ogImage: HERO_IMAGE,
    },
    jsonLd: webPageSchema(
      'AllChinaBuy Spreadsheet Categories',
      'Browse every AllChinaBuy Spreadsheet category with direct links to shoes, hoodies, t-shirts, jackets, bags, accessories and more.',
      '/spreadsheet'
    ),
  },
  '/guides': {
    seo: {
      title: 'AllChinaBuy Guides & Articles — QC, Shipping, Safety & Buying Tips',
      description:
        'Read original AllChinaBuy guides covering the spreadsheet, QC photos, shipping costs, warehouse, coupons, safety and the full buying process.',
      keywords:
        'AllChinaBuy guides, AllChinaBuy QC, AllChinaBuy shipping cost, AllChinaBuy buying guide, AllChinaBuy spreadsheet guide',
      canonical: SITE_URL + '/guides',
      ogType: 'website',
      ogImage: HERO_IMAGE,
    },
    jsonLd: webPageSchema(
      'AllChinaBuy Guides & Articles',
      'Original, practical AllChinaBuy guides covering the spreadsheet, QC photos, shipping, safety and buying tips.',
      '/guides'
    ),
  },
  '/reviews': {
    seo: {
      title: 'AllChinaBuy Reviews — Independent Research & Buying Insights',
      description:
        'Independent AllChinaBuy research and reviews covering legitimacy, safety, pros and cons, and how to evaluate the platform honestly before ordering.',
      keywords:
        'AllChinaBuy review, AllChinaBuy legit, AllChinaBuy safe, AllChinaBuy pros and cons',
      canonical: SITE_URL + '/reviews',
      ogType: 'website',
      ogImage: HERO_IMAGE,
    },
    jsonLd: webPageSchema(
      'AllChinaBuy Reviews & Independent Research',
      'Honest, independent AllChinaBuy insights to help you evaluate the platform before you order.',
      '/reviews'
    ),
  },
  '/safety': {
    seo: {
      title: 'AllChinaBuy Safety Guide — Verify Links, QC & Shipping Risks',
      description:
        'A practical AllChinaBuy safety checklist covering link verification, seller checks, QC photos, sizing, shipping risks and counterfeit awareness.',
      keywords:
        'AllChinaBuy safe, AllChinaBuy safety, AllChinaBuy verify links, AllChinaBuy QC check',
      canonical: SITE_URL + '/safety',
      ogType: 'website',
      ogImage: HERO_IMAGE,
    },
    jsonLd: webPageSchema(
      'AllChinaBuy Safety Guide',
      'A practical checklist to verify information and reduce common AllChinaBuy buying risks.',
      '/safety'
    ),
  },
};

export function resolvePageSeo(pathname: string): PageSeo | null {
  if (staticPages[pathname]) return staticPages[pathname];

  if (pathname.startsWith('/article/')) {
    const slug = pathname.replace('/article/', '').replace(/\/$/, '');
    const article = articles.find((a) => a.slug === slug);
    if (article) {
      return {
        seo: {
          title: article.title,
          description: article.meta,
          keywords: 'AllChinaBuy Spreadsheet, AllChinaBuy guide, AllChinaBuy',
          canonical: SITE_URL + '/article/' + slug,
          ogType: 'article',
          ogImage: article.image,
          twitterImage: article.image,
        },
        jsonLd: articleSchema(slug, article.title, article.meta, article.image),
      };
    }
  }

  return null;
}